import styled from '@emotion/styled'
import {
  fetchCustomerTpls,
  retroClaimTplsPointsShop,
  selectBrand,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  Input,
  Loading,
  PageTitle,
  FormWrapper,
  Button,
  Body,
  Heading,
} from 'components'
import { RequestError } from '@open-tender/types'
import { usePointsShop, useTplsPointsShop } from 'hooks'
import { useEffect, useState } from 'react'
import { isPennStationBrand } from 'utils/helpers'

// Shown only when the API gives us no reason for the failure. Otherwise we
// surface the `detail` returned by the loyalty service (e.g. invalid receipt
// ID, invalid subtotal, retro claim limit reached).
const CLAIM_ERROR_FALLBACK = 'Failed to claim points'

const LoadingContainer = styled.div`
  height: 100px;
`

const SubtotalHelper = styled(Body)`
  display: block;
  text-align: left;
  margin-top: 0.5rem;
`

const ClaimPointsForm = ({
  callback,
}: {
  callback?: (claimedPoints: number) => void
}) => {
  const dispatch = useAppDispatch()
  const [receiptId, setReceiptId] = useState('')
  const [subtotal, setSubtotal] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { tpls } = useAppSelector(selectBrand) || {}
  const hasTplPointsShop =
    tpls === 'COMO' || tpls === 'PUNCHH' || tpls === 'SPARKFLY'
  const usePointsShopHook = hasTplPointsShop ? useTplsPointsShop : usePointsShop
  const { pointsBalance } = usePointsShopHook()

  useEffect(() => {
    dispatch(fetchCustomerTpls())
  }, [dispatch])

  const handleCallback = async () => {
    const currentPoints = pointsBalance
    const getLatestPoints = await dispatch(fetchCustomerTpls())
    if (
      getLatestPoints.type.endsWith('fulfilled') &&
      getLatestPoints.payload &&
      'points' in getLatestPoints.payload
    ) {
      const newBalance = getLatestPoints.payload?.points?.balance || 0
      const claimedPoints = newBalance - (currentPoints || 0)
      callback?.(claimedPoints)
    }
  }

  const handleClaim = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await dispatch(
        retroClaimTplsPointsShop({
          receiptId,
          subtotal,
        })
      )
      if (response.type.endsWith('fulfilled')) {
        setReceiptId('')
        setSubtotal('')
        await handleCallback()
      } else {
        const err = response.payload as RequestError
        setError(err?.detail || CLAIM_ERROR_FALLBACK)
      }
    } catch {
      setError(CLAIM_ERROR_FALLBACK)
    } finally {
      setLoading(false)
    }
  }

  const title = 'Claim Points'
  const subtitle =
    "Enter the 'Reciept ID' from your printed receipt to claim missed points."
  const isDisabled = !receiptId || !subtotal || loading

  const PointsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
  `

  const PointItem = styled.div`
    display: flex;
    column-gap: 10px;
    align-items: center;
    text-align: left;
  `

  const LisItem = ({ title, content }: { title?: string; content: string }) => {
    return (
      <PointItem>
        {title ? <Body>•</Body> : null}
        <div>
          {title ? (
            <Heading style={{ textTransform: 'none' }}>{title}: </Heading>
          ) : null}
          <Body>{content}</Body>
        </div>
      </PointItem>
    )
  }

  const ClaimPointsSubheading = () => {
    return (
      <PointsList>
        <LisItem
          content={
            'Enter the details from your receipt to add points that were not applied while ordering.'
          }
        />
        <LisItem
          title={'Receipt ID'}
          content="Enter the number with a hyphen (e.g.
            555555-555)."
        />
        <LisItem
          title={'Subtotal'}
          content="Enter the amount with a decimal (e.g. 55.55)."
        />
        <LisItem
          title={'Limitations'}
          content="2 receipts per day, 4 per 30-day period,
            per account."
        />
      </PointsList>
    )
  }

  return (
    <FormWrapper>
      <PageTitle
        title={title}
        subtitle={isPennStationBrand ? <ClaimPointsSubheading /> : subtitle}
        style={isPennStationBrand ? { marginBottom: 20 } : undefined}
      />
      {loading ? (
        <LoadingContainer>
          <Loading text="Claiming points..." />
        </LoadingContainer>
      ) : (
        <>
          <Input
            name="receiptId"
            type="text"
            label="Receipt ID"
            value={receiptId}
            onChange={(event) => setReceiptId(event.target.value)}
            error={error}
          />
          <Input
            name="subtotal"
            type="number"
            label="Subtotal"
            value={subtotal}
            onChange={(event) => setSubtotal(event.target.value)}
          >
            <SubtotalHelper color="secondary" size="small">
              Please add the subtotal from the order receipt you wish to claim
              points for.
            </SubtotalHelper>
          </Input>

          <Button onClick={handleClaim} disabled={isDisabled}>
            Claim
          </Button>
        </>
      )}
    </FormWrapper>
  )
}

export default ClaimPointsForm
